import { useState, ReactNode } from "react";
import styles from "../editQuiz.module.css";
import { VerityInstituteSettings } from "../../types/common";
import { Button, Link, Typography } from "@mui/material";
import Checkmark from "../images/checkmark-icon.svg?react";
import CaretUp from "../images/caretUp.svg?react";
import CaretDown from "../images/caretDown.svg?react";
import { useMobileLayout } from "../../hooks";
import {
	ProctoringOption,
	Application,
	ProctoringPlatforms,
	OptionRelatedInstituteSettings,
	Extension,
} from "@verity/proctoring-option/types";
import {
	DEFAULT_ENABLED_PROCTORING_CONFIGURATION_VALUES,
	DEFAULT_OPTION_RELATED_INSTITUTE_SETTINGS,
	DEFAULT_OPTION_RELATED_VERITY_INSTITUTE_SETTINGS,
	getProctoringOptionDisabledDetails,
	isOptionSupportedForPlatform,
	PROCTORING_OPTION_INFO,
} from "@verity/proctoring-option";
import { PROCTORING_OPTION_TEXT } from "./option-text";
import { EllipsisTooltip } from "./option-modals/listEditor";
import i18nPackage from "../../i18n/i18n";
import type { VerityApi } from "../../api/fetch";

export type NestedOption = {
	text: string;
	children?: NestedOption[];
};

export function filterOptionsByPlatform(
	option: Partial<ProctoringOption>,
	proctoringPlatforms: (keyof ProctoringPlatforms)[],
): (keyof ProctoringOption)[] {
	const filteredOptions: (keyof ProctoringOption)[] = [];
	const excludedKeys = new Set<keyof ProctoringOption>(["isExtension", "isLockdown", "isMobile"]);

	// Iterate over entries and ensure keys are treated as keyof ProctoringOption
	for (const [key, value] of Object.entries(option) as [
		keyof ProctoringOption,
		(typeof option)[keyof ProctoringOption],
	][]) {
		if (excludedKeys.has(key)) {
			// Skip the excluded keys
			continue;
		}

		if (!proctoringPlatforms.some((platform) => isOptionSupportedForPlatform(platform, key, value))) {
			continue;
		}

		if (typeof value === "boolean" && value) {
			// If the value is boolean and true, add the key to the list
			filteredOptions.push(key);
		} else if (typeof value === "string" && value !== "off") {
			// If the value is a string and not 'off', add the key to the list
			filteredOptions.push(key);
		} else if (Array.isArray(value) && value.length > 0) {
			// If the value is an array and not empty, add the key to the list
			filteredOptions.push(key);
		} else if (key === "flaggedKeywords" && value != null) {
			filteredOptions.push(key);
		}
	}

	return filteredOptions;
}

export function categorizeProctoringOptions(
	option: Partial<ProctoringOption>,
	verityInstituteSettings?: VerityInstituteSettings,
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings,
	fetchedInstituteSettings?: Awaited<ReturnType<VerityApi["getInstituteSettings"]>>,
): {
	optionCategories: Record<string, (keyof ProctoringOption)[]>;
	optionChildren: Map<keyof ProctoringOption, NestedOption[]>;
} {
	const proctoringPlatformsKey: (keyof ProctoringPlatforms)[] = [];

	if (option.isExtension) {
		if (!verityInstituteSettings || verityInstituteSettings.allowExtensionPlatform) {
			proctoringPlatformsKey.push("isExtension");
		}
	}
	if (option.isLockdown) {
		if (!verityInstituteSettings || verityInstituteSettings.allowLockdownPlatform) {
			proctoringPlatformsKey.push("isLockdown");
		}
	}
	if (option.isMobile) {
		if (!verityInstituteSettings || verityInstituteSettings.allowMobilePlatform) {
			proctoringPlatformsKey.push("isMobile");
		}
	}

	const activeOptions = filterOptionsByPlatform(option, proctoringPlatformsKey);

	const optionCategories = activeOptions.reduce(
		(acc, key) => {
			if (key === "excludedWebTrafficRecordingUrls" && !option.recordWebTraffic) {
				return acc;
			}
			const category = PROCTORING_OPTION_TEXT[key]?.category;
			if (category) {
				if (!acc[category]) {
					acc[category] = [];
				}
				acc[category].push(key);
			}
			return acc;
		},
		{} as Record<string, (keyof ProctoringOption)[]>,
	);

	const getStrictModeEnabled = (optionKey: keyof ProctoringOption) =>
		verityInstituteSettings &&
		optionRelatedInstituteSettings &&
		getProctoringOptionDisabledDetails(
			optionKey,
			"strict",
			option,
			verityInstituteSettings,
			optionRelatedInstituteSettings,
		)?.allowedValues.includes("strict");

	const optionChildren = activeOptions.reduce((acc, key) => {
		const value = option[key];
		if (value && typeof value === "string") {
			if (!["objectDetectionMode"].includes(key)) {
				acc.set(key, []);
				if (key === "applicationBlockMode") {
					if (activeOptions.includes("blockWindowedApplications")) {
						if (activeOptions.includes("hideOtherWindows")) {
							acc.get(key)!.push({
								text: i18nPackage.t(
									"editQuizPage.configurationStep.restrictApplicationsTSX.blockApplications.modes.all",
								),
							});
						} else {
							acc.get(key)!.push({
								text: i18nPackage.t(
									"editQuizPage.configurationStep.restrictApplicationsTSX.blockApplications.modes.allow-list",
								),
							});
						}
					} else {
						acc.get(key)!.push({
							text: i18nPackage.t(
								"editQuizPage.configurationStep.restrictApplicationsTSX.blockApplications.modes.block-list",
							),
						});
					}
				}

				if (key === "extensionBlockMode") {
					if (activeOptions.includes("allowedExtensionsList")) {
						acc.get(key)!.push({
							text: i18nPackage.t("editQuizPage.configurationStep.disableFeaturesTSX.blockExtensions.allow-list"),
						});
					} else {
						acc.get(key)!.push({
							text: i18nPackage.t("editQuizPage.configurationStep.disableFeaturesTSX.blockExtensions.all"),
						});
					}
				}

				if (key === "disableClipboardMode") {
					if (value === "system") {
						acc.get(key)!.push({
							text: i18nPackage.t("editQuizPage.confirmationStep.configurationSummaryListTSX.systemClipboard"),
						});
					}
				} else if (key === "enableCalculator") {
					const calc = option["enableCalculator"];
					const type =
						calc === "scientific"
							? "Standard and scientific"
							: calc
								? calc.charAt(0).toUpperCase() + calc.slice(1)
								: "";
					acc.get(key)!.push({
						text: i18nPackage.t("editQuizPage.confirmationStep.configurationSummaryListTSX.calculator", {
							type: type,
						}),
					});
				} else if (value !== "strict") {
					acc
						.get(key)!
						.push({ text: i18nPackage.t("editQuizPage.confirmationStep.configurationSummaryListTSX.bypassMode") });
				}
				if (key === "videoRecordingMode") {
					if (activeOptions.includes("faceTrackingMode")) {
						if (option["faceTrackingMode"]) {
							acc.get(key)!.push({
								text: i18nPackage.t("editQuizPage.confirmationStep.configurationSummaryListTSX.multiplePeople"),
								children:
									option["faceTrackingMode"] === "normal" && getStrictModeEnabled("faceTrackingMode")
										? [{ text: i18nPackage.t("editQuizPage.confirmationStep.configurationSummaryListTSX.bypassMode") }]
										: undefined,
							});
						}
					}
					if (activeOptions.includes("objectDetectionMode")) {
						const objectDetectionValue = option["objectDetectionMode"];
						if (objectDetectionValue)
							acc.get(key)!.push({
								text: i18nPackage.t("editQuizPage.confirmationStep.configurationSummaryListTSX.suspiciousObjects"),
							});
					}
				} else if (key === "audioRecordingMode") {
					if (activeOptions.includes("quietEnvironment")) {
						const quietEnvironmentValue = option["quietEnvironment"];
						if (quietEnvironmentValue)
							acc
								.get(key)!
								.push({ text: i18nPackage.t("editQuizPage.confirmationStep.configurationSummaryListTSX.quietEnv") });
					}
					if (activeOptions.includes("audioTranscription")) {
						const audioTranscriptionValue = option["audioTranscription"];
						if (audioTranscriptionValue)
							acc.get(key)!.push({
								text: i18nPackage.t("editQuizPage.confirmationStep.configurationSummaryListTSX.audioTranscription"),
							});

						if (activeOptions.includes("flaggedKeywords")) {
							const totalAssessmentFlaggedKeywords = option["flaggedKeywords"]?.length ?? 0;
							const totalInstituteFlaggedKeywords = fetchedInstituteSettings?.flaggedKeywords?.length ?? 0;
							const totalFlaggedKeywords = totalAssessmentFlaggedKeywords + totalInstituteFlaggedKeywords;
							if (totalFlaggedKeywords)
								acc.get(key)!.push({
									text: `${i18nPackage.t("editQuizPage.confirmationStep.configurationSummaryListTSX.flaggedKeywords")} (${totalFlaggedKeywords})`,
								});
						}
					}
				}
			}
		} else if (value && typeof value === "boolean") {
			if (key === "idVerification") {
				acc.set(key, []);
				if (activeOptions.includes("idVerificationEnableAi")) {
					if (option["idVerificationEnableAi"]) {
						acc.get(key)!.push({
							text: i18nPackage.t("editQuizPage.confirmationStep.configurationSummaryListTSX.aiIDVer"),
							children:
								activeOptions.includes("idVerificationMode") &&
								option["idVerificationMode"] === "normal" &&
								getStrictModeEnabled("idVerificationMode")
									? [{ text: i18nPackage.t("editQuizPage.confirmationStep.configurationSummaryListTSX.bypassMode") }]
									: undefined,
						});
					}
				}
			}
			if (key === "recordInput") {
				if (activeOptions.includes("keyboardShortcutBlockList")) {
					const keyboardListValue = option["keyboardShortcutBlockList"];
					if (keyboardListValue && keyboardListValue.length > 0) {
						acc.set(
							key,
							keyboardListValue.map((shortcut) => ({ text: shortcut })),
						);
					}
				}
			}
		} else if (Array.isArray(value) && value.every((item) => typeof item === "string")) {
			if (key === "applicationBlockList") {
				if (
					activeOptions.includes("applicationBlockMode") &&
					!activeOptions.includes("blockWindowedApplications") &&
					!activeOptions.includes("hideOtherWindows")
				) {
					acc.set(
						key,
						value.map((entry) => ({ text: entry })),
					);
				}
			} else if (key === "allowedApplicationsList") {
				if (
					activeOptions.includes("applicationBlockMode") &&
					activeOptions.includes("blockWindowedApplications") &&
					!activeOptions.includes("hideOtherWindows")
				) {
					acc.set(
						key,
						value.map((entry) => ({ text: entry })),
					);
				}
			} else if (key === "allowedExtensionsList") {
				if (activeOptions.includes("extensionBlockMode")) {
					acc.set(
						key,
						value.map((entry) => ({ text: entry })),
					);
				}
			} else {
				acc.set(
					key,
					value.map((entry) => ({ text: entry })),
				);
			}
		} else if (Array.isArray(value)) {
			if (key === "flaggedKeywords") {
				if (activeOptions.includes("flaggedKeywords")) {
					acc.set(key, [{ text: i18nPackage.t("editQuizPage.confirmationStep.configurationSummaryListTSX.aiIDVer") }]);
				}
			}
		}
		return acc;
	}, new Map<keyof ProctoringOption, NestedOption[]>());

	return { optionCategories, optionChildren };
}

export function shouldShowProctoringOption(
	option: keyof ProctoringOption,
	proctoringOptions?: Partial<ProctoringOption>,
	verityInstituteSettings = DEFAULT_OPTION_RELATED_VERITY_INSTITUTE_SETTINGS,
	instituteSettings = DEFAULT_OPTION_RELATED_INSTITUTE_SETTINGS,
	enabledConfigurationValues = DEFAULT_ENABLED_PROCTORING_CONFIGURATION_VALUES,
) {
	const optionInfo = PROCTORING_OPTION_INFO[option];
	const onValues = optionInfo?.isList ? [[""]] : (optionInfo?.onValues ?? []);
	return onValues.some((onValue) => {
		const disabledDetails = getProctoringOptionDisabledDetails(
			option,
			onValue,
			proctoringOptions,
			verityInstituteSettings,
			instituteSettings,
			enabledConfigurationValues,
		);
		return !Object.keys(disabledDetails.disabledReasons).some(
			(reason) => reason === "platformUnsupported" || reason === "liteMode" || reason === "platformNotAllowed",
		);
	});
}

export const CollapsibleList = ({ items, maxVisible = 5 }: { items: ReactNode[]; maxVisible?: number }) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const visibleCount = isExpanded ? items.length : maxVisible;
	const hiddenCount = items.length - visibleCount;

	return (
		<>
			{items.slice(0, visibleCount)}
			{items.length > maxVisible && (
				<Button
					variant="text"
					className={styles.button}
					onClick={() => setIsExpanded(!isExpanded)}
					endIcon={isExpanded ? <CaretUp /> : <CaretDown />}
				>
					{isExpanded ? "Show less" : `${hiddenCount} more`}
				</Button>
			)}
		</>
	);
};

export function getUniqueApplicationNamesAndIcons(
	applicationNames: string[],
	applications: Application[],
): Record<string, string | undefined> {
	const matchedApps: Record<string, string | undefined> = {};
	const uniqueApps = new Set<string>();

	applicationNames.forEach((appName) => {
		const matchedApp = applications.find((app) => {
			return app.processName.includes(appName);
		});
		const matchedAppName = matchedApp ? matchedApp.name : appName;
		const matchedAppIcon = matchedApp ? matchedApp.iconSvg : undefined;

		if (!uniqueApps.has(matchedAppName)) {
			uniqueApps.add(matchedAppName);
			matchedApps[matchedAppName] = matchedAppIcon;
		}
	});

	return matchedApps;
}

function CollapsibleApplicationList({
	allApplications,
	processNames,
}: {
	allApplications: Application[];
	processNames: string[];
}) {
	return (
		<CollapsibleList
			items={Object.entries(getUniqueApplicationNamesAndIcons(processNames, allApplications)).map(
				([name, icon], index) => (
					<div
						key={index}
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: "10px",
						}}
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
							}}
						>
							<span style={{ minWidth: "20px" }}>{index + 1}.</span>
							<div
								key={name}
								style={{
									display: "flex",
									alignItems: "center",
									gap: "10px",
								}}
							>
								{icon && (
									<div
										style={{
											width: 24,
											height: 24,
										}}
										dangerouslySetInnerHTML={{ __html: icon }}
									></div>
								)}
								<span
									style={{
										fontFamily: "Inter",
										fontWeight: 400,
										fontSize: 14,
										lineHeight: "160%",
										letterSpacing: 0,
									}}
								>
									{name}
								</span>
							</div>
						</div>
					</div>
				),
			)}
		/>
	);
}

function CollapsibleExtensionList({
	allExtensions,
	customExtensions,
}: {
	allExtensions: Extension[];
	customExtensions: string[];
}) {
	// Convert string[] → Extension[]
	function convertCustomExtensions(customList: string[], allExtensions: Extension[]): Extension[] {
		return customList.map((entry) => {
			const id = entry.slice(-32);
			const name = entry.slice(0, -32);

			// Try to find preset extension by ID
			const preset = allExtensions.find((ext) => ext.id === id);

			return {
				id,
				name: preset ? preset.name : name, // prefer real name
				iconSvg: preset ? preset.iconSvg : undefined, // only use real icon if match
				type: preset ? preset.type : "Other",
			};
		});
	}

	const mergedExtensions = convertCustomExtensions(customExtensions, allExtensions);

	return (
		<CollapsibleList
			items={mergedExtensions.map((ext, index) => (
				<div
					key={index}
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: "8px",
					}}
				>
					<div style={{ display: "flex", alignItems: "center" }}>
						<span style={{ minWidth: "20px" }}>{index + 1}.</span>

						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "10px",
							}}
						>
							{ext.iconSvg && (
								<div
									style={{
										width: 24,
										height: 24,
									}}
									dangerouslySetInnerHTML={{ __html: ext.iconSvg }}
								></div>
							)}
							<span
								style={{
									fontFamily: "Inter",
									fontWeight: 400,
									fontSize: 14,
									lineHeight: "160%",
									letterSpacing: 0,
								}}
							>
								{ext.name}
							</span>
						</div>
					</div>
				</div>
			))}
		/>
	);
}

function CheckedSubOption({ children }: { children: ReactNode }) {
	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: "10px",
			}}
		>
			<Checkmark aria-hidden={true} />
			{children}
		</div>
	);
}

export function ConfigurationSummaryList({
	optionCategories,
	optionChildren,
	applications,
	extensions,
}: {
	optionCategories: Record<string, (keyof ProctoringOption)[]>;
	optionChildren: Map<string, NestedOption[]>;
	applications: Application[];
	extensions: Extension[];
}) {
	const isMobileLayout = useMobileLayout();

	const renderNestedChildren = (items: NestedOption[]) => {
		return (
			<>
				{items.map((item, index) => (
					<div
						key={index}
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "10px",
						}}
					>
						<CheckedSubOption>{item.text}</CheckedSubOption>
						{item.children && item.children.length > 0 && (
							<div style={{ marginLeft: "20px" }}>{renderNestedChildren(item.children)}</div>
						)}
					</div>
				))}
			</>
		);
	};

	const renderOptionChildren = (currentOptionKey: keyof ProctoringOption) => {
		if (optionChildren.get(currentOptionKey)!.length === 0) return null;

		const usesCollapsibleList = [
			"tabWhiteList",
			"tabBlockList",
			"excludedWebTrafficRecordingUrls",
			"recordInput",
		].includes(currentOptionKey);

		if (usesCollapsibleList)
			return (
				<CollapsibleList
					items={
						currentOptionKey === "recordInput"
							? (optionChildren.get(currentOptionKey) || []).map((item, index) => <div key={index}>{item.text}</div>)
							: (optionChildren.get(currentOptionKey) || []).map((item, index) => (
									<div
										key={index}
										style={{
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											width: "100%",
											maxWidth: "100%",
											gap: "10px",
										}}
									>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												width: "100%",
												maxWidth: "100%",
											}}
										>
											<span style={{ minWidth: "20px" }}>{index + 1}.</span>
											<EllipsisTooltip key={index}>
												<Link
													style={{ maxWidth: isMobileLayout ? "90%" : "620px" }}
													href={item.text}
													target="_blank"
													rel="noreferrer"
												>
													{item.text}
												</Link>
											</EllipsisTooltip>
										</div>
									</div>
								))
					}
				/>
			);

		return renderNestedChildren(optionChildren.get(currentOptionKey)!);
	};

	const renderApplicationBlockList = (currentOptionKey: keyof ProctoringOption) => {
		if (currentOptionKey === "applicationBlockMode") {
			return (
				<>
					<div style={{ fontWeight: 600 }}>{optionChildren.get(currentOptionKey)?.[0]?.text}</div>
					{optionChildren.get(currentOptionKey)?.[0]?.children?.map((item, index) => (
						<CheckedSubOption key={`${currentOptionKey}-${index}`}>{item.text}</CheckedSubOption>
					))}
					{optionChildren.has("applicationBlockList") && (
						<CollapsibleApplicationList
							allApplications={applications}
							processNames={optionChildren.get("applicationBlockList")?.map((item) => item.text) || []}
						/>
					)}
					{optionChildren.has("allowedApplicationsList") && (
						<CollapsibleApplicationList
							allApplications={applications}
							processNames={optionChildren.get("allowedApplicationsList")?.map((item) => item.text) || []}
						/>
					)}
				</>
			);
		}
		return null;
	};

	const renderExtensionAllowList = (currentOptionKey: keyof ProctoringOption) => {
		if (currentOptionKey === "extensionBlockMode") {
			return (
				<>
					<div style={{ fontWeight: 600 }}>{optionChildren.get(currentOptionKey)?.[0]?.text}</div>
					{optionChildren.get(currentOptionKey)?.[0]?.children?.map((item, index) => (
						<CheckedSubOption key={`${currentOptionKey}-${index}`}>{item.text}</CheckedSubOption>
					))}
					{optionChildren.has("allowedExtensionsList") && (
						<CollapsibleExtensionList
							allExtensions={extensions}
							customExtensions={optionChildren.get("allowedExtensionsList")?.map((item) => item.text) || []}
						/>
					)}
				</>
			);
		}
		return null;
	};

	return Object.entries(optionCategories).map(([category, keys], i) => (
		<div
			key={i}
			style={{
				width: "100%",
				paddingBottom: "10px",
				paddingRight: "20px",
				fontSize: "14px",
				lineHeight: "1.6",
			}}
		>
			<Typography variant="h4" fontSize="14px" fontWeight="700">
				{category}
			</Typography>
			{keys.map((optionKey, index) => {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				const optionData = PROCTORING_OPTION_TEXT[optionKey];
				return (
					<div key={index} style={{ padding: "10px 0px", color: "#000" }}>
						<div
							style={{
								display: "flex",
								alignItems: "start",
								padding: "4px 0",
								paddingLeft: "4px",
								gap: "20px",
							}}
						>
							{isMobileLayout ? (
								<>
									<div
										style={{
											fontWeight: "700",
											flexBasis: "35%",
											display: "flex",
											alignItems: "center",
											gap: "6px",
											maxWidth: "15px",
											height: "fit-content",
											justifyContent: "start",
											alignContent: "center",
										}}
									>
										<div
											style={{
												width: "26px",
												height: "26px",
												justifyContent: "start",
												color: "#42296E",
											}}
										>
											{optionData.icon && <optionData.icon width={26} height={26} fill={"#42296E"} />}
										</div>
									</div>
									<div
										style={{
											display: "flex",
											flexDirection: "column",
											alignItems: "start",
											maxWidth: "100%",
											gap: "5px",
											marginTop: "2px",
										}}
									>
										<div
											style={{
												display: "flex",
												justifyContent: "start",
												alignItems: "start",
												width: "165px",
												height: "fit-content",
											}}
										>
											{optionData.header}
										</div>
										<div
											style={{
												flexBasis: "75%",
												display: "flex",
												alignItems: "center",
												fontWeight: 400,
												color: "black",
												paddingTop: "0px",
											}}
										>
											{optionData.description}
										</div>
										{optionChildren?.has(optionKey) && (
											<>
												{renderOptionChildren(optionKey)}
												{renderApplicationBlockList(optionKey)}
												{renderExtensionAllowList(optionKey)}
											</>
										)}
									</div>
								</>
							) : (
								<>
									<div
										style={{
											fontWeight: "700",
											flexBasis: "35%",
											display: "flex",
											alignItems: "center",
											gap: "6px",
											maxWidth: "200px",
											height: "fit-content",
											justifyContent: "start",
											alignContent: "center",
										}}
									>
										<div
											style={{
												width: "26px",
												height: "26px",
												justifyContent: "start",
												color: "#42296E",
											}}
										>
											{optionData.icon && <optionData.icon width={26} height={26} fill={"#42296E"} />}
										</div>
										<div
											style={{
												display: "flex",
												justifyContent: "start",
												alignItems: "start",
												width: "165px",
												height: "fit-content",
											}}
										>
											{optionData.header}
										</div>
									</div>
									<div
										style={{
											display: "flex",
											flexDirection: "column",
											alignItems: "start",
											gap: "10px",
										}}
									>
										<div
											style={{
												flexBasis: "75%",
												display: "flex",
												alignItems: "center",
												fontWeight: 400,
												color: "black",
												paddingTop: "3px",
											}}
										>
											{optionData.description}
										</div>
										{optionChildren?.has(optionKey) && (
											<>
												{renderOptionChildren(optionKey)}
												{renderApplicationBlockList(optionKey)}
												{renderExtensionAllowList(optionKey)}
											</>
										)}
									</div>
								</>
							)}
						</div>
					</div>
				);
			})}
		</div>
	));
}
